import React, { useEffect } from 'react';
import Layout from "../temp/layout/Layout";
import { Helmet } from 'react-helmet';
import AOS from 'aos';
import 'aos/dist/aos.css';
import { Link } from 'react-router-dom';
import { 
    FaSchool, FaUniversity, FaChalkboardTeacher,
    FaCheckCircle, FaArrowRight
} from 'react-icons/fa';

const Services = () => {
    useEffect(() => {
        AOS.init({ 
            duration: 1000, 
            once: true,
            offset: 100
        });
    }, []);

    const services = [
        {
            icon: <FaSchool className="text-5xl" />,
            title: "School Management Solutions",
            description: "Complete ERP solution designed specifically for schools to manage all administrative and academic operations efficiently.",
            features: ["Student Management", "Teacher Management", "Attendance Tracking", "Fee Management", "Exam Management"]
        },
        {
            icon: <FaUniversity className="text-5xl" />,
            title: "College Management Solutions",
            description: "Enterprise-grade ERP system for colleges and universities with advanced features for complex academic structures.",
            features: ["Course Management", "Semester System", "Credit Management", "Research Tracking", "Alumni Management"]
        },
        {
            icon: <FaChalkboardTeacher className="text-5xl" />,
            title: "Coaching Institute Solutions",
            description: "Specialized ERP for coaching centers and training academies with batch management and performance tracking.",
            features: ["Batch Management", "Performance Tracking", "Mock Tests", "Progress Reports", "Student Analytics"]
        }
    ];

    return (
        <Layout>
            <Helmet>
                <title>Services - Educational Eternity | Complete Education ERP Services</title>
                <meta name="description" content="Comprehensive education ERP services including school management, college management, mobile apps, cloud hosting, and 24/7 support." />
            </Helmet>

            {/* Hero Section */}
            <section className="relative w-full h-[50vh] bg-gradient-to-br from-blue-600 via-indigo-700 to-purple-800 flex items-center justify-center overflow-hidden">
                <div className="absolute inset-0 opacity-20" style={{
                    backgroundImage: `url(assets/images/home1.jpg)`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center'
                }}></div>
                <div className="relative z-10 text-center px-4">
                    <h1 data-aos="fade-up" className="text-5xl md:text-7xl font-bold text-white mb-4">
                        Our Services
                    </h1>
                    <p data-aos="fade-up" data-aos-delay="100" className="text-xl md:text-2xl text-blue-100 max-w-3xl mx-auto">
                        Comprehensive solutions for all your educational institution needs
                    </p>
                </div>
            </section>

            {/* Services Grid */}
            <section className="py-20 bg-gray-50">
                <div className="container mx-auto px-4">
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
                        {services.map((service, idx) => (
                            <div
                                key={idx}
                                data-aos="fade-up"
                                data-aos-delay={idx * 100}
                                className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border border-gray-100"
                            >
                                <div className="inline-flex p-4 rounded-xl bg-gradient-to-r from-blue-500 to-indigo-600 text-white mb-6">
                                    {service.icon}
                                </div>
                                <h3 className="text-2xl font-bold text-gray-900 mb-4">{service.title}</h3>
                                <p className="text-gray-600 leading-relaxed mb-6">{service.description}</p>
                                <ul className="space-y-2">
                                    {service.features.map((feature, i) => (
                                        <li key={i} className="flex items-center text-gray-700">
                                            <FaCheckCircle className="text-green-500 mr-2 flex-shrink-0" />
                                            <span className="text-sm">{feature}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-20 bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600">
                <div className="container mx-auto px-4 text-center" data-aos="fade-up">
                    <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
                        Ready to Get Started?
                    </h2>
                    <p className="text-xl text-blue-100 mb-8 max-w-3xl mx-auto">
                        Contact us today to learn more about our services and how we can help transform your institution.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                        <Link to="/contact">
                            <button className="px-8 py-4 bg-white text-blue-600 rounded-full text-lg font-bold shadow-2xl hover:shadow-white/50 transform hover:scale-105 transition-all duration-300 flex items-center gap-2 mx-auto">
                                Contact Us <FaArrowRight />
                            </button>
                        </Link>
                        <Link to="/plans">
                            <button className="px-8 py-4 bg-transparent border-2 border-white text-white rounded-full text-lg font-bold hover:bg-white hover:text-blue-600 transform hover:scale-105 transition-all duration-300">
                                View Plans
                            </button>
                        </Link>
                    </div>
                </div>
            </section>
        </Layout>
    );
};

export default Services;

